import React, { useEffect, useRef, useState } from "react";
import { authFetch } from "../../../../utils/cmsAuth";
import useUploadSuccessAlert from "../useUploadSuccessAlert";
import "../YoutubeLab/YoutubeLab.css";
import "./ThreadsLab.css";

const BASE = "/concierge/threads-lab";
const RUNNING = new Set(["QUEUED", "PREPARING", "CREATING", "PUBLISHING"]);
const isPublished = (job) => job.status === "PUBLISHED" && Boolean(job.post_id);
const LABELS = { QUEUED: "접수 완료", PREPARING: "계정 확인·AI 본문 생성 중", CREATING: "미디어 준비 중",
  PUBLISHING: "Threads 게시 중", PUBLISHED: "게시 완료", FAILED: "게시 전 준비 실패", NEEDS_REVIEW: "게시 여부 확인 필요", REVIEWED: "관리자 확인 완료" };

async function api(path, options = {}) {
  const response = await authFetch(`${BASE}${path}`, { ...options, headers: { "Content-Type": "application/json", ...options.headers } });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(typeof data.detail === "string" ? data.detail : "요청을 처리하지 못했습니다.");
  return data;
}

function newId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function when(value) {
  return value ? new Date(value).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : "-";
}

function Photo({ path }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    let url;
    setSrc("");
    authFetch(`${BASE}${path}`, { signal: controller.signal }).then(async (response) => {
      if (!response.ok) return;
      const blob = await response.blob();
      if (controller.signal.aborted) return;
      url = URL.createObjectURL(blob); setSrc(url);
    }).catch(() => {});
    return () => { controller.abort(); if (url) URL.revokeObjectURL(url); };
  }, [path]);
  return src ? <img src={src} alt="" loading="lazy" /> : <span className="cyl-thumbnail-empty">미리보기 준비 중 / 없음</span>;
}

export default function ThreadsLab() {
  const [config, setConfig] = useState(null);
  const [gallery, setGallery] = useState({ items: [], total_pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [includeVideo, setIncludeVideo] = useState(true);
  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imageIds, setImageIds] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [job, setJob] = useState(null);
  const [revision, setRevision] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(null);
  const submitting = useRef(false);
  const alive = useRef(true);
  const trackSuccess = useUploadSuccessAlert(job, isPublished, "쓰레드");

  useEffect(() => {
    alive.current = true;
    const controller = new AbortController();
    api("/configuration", { signal: controller.signal }).then(setConfig)
      .catch((e) => { if (!controller.signal.aborted) setError(e.message); });
    return () => { alive.current = false; controller.abort(); };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api(`/media?page=${page}`, { signal: controller.signal }).then((data) => {
      if (!controller.signal.aborted) { setGallery(data); if (data.page && data.page !== page) setPage(data.page); }
    }).catch((e) => { if (!controller.signal.aborted) setError(e.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [page]);

  useEffect(() => {
    const controller = new AbortController();
    api("/jobs", { signal: controller.signal }).then((data) => {
      if (!controller.signal.aborted) {
        setJobs(data.items || []);
        setJob((current) => current || (data.items || []).find((item) => RUNNING.has(item.status)) || null);
      }
    }).catch((e) => { if (!controller.signal.aborted) setError(e.message); });
    return () => controller.abort();
  }, [revision]);

  useEffect(() => {
    setImages([]);
    if (!selected) return undefined;
    const controller = new AbortController();
    setImagesLoading(true);
    api(`/stores/${selected.user_id}/images`, { signal: controller.signal }).then((data) => {
      if (!controller.signal.aborted) setImages(data.images || []);
    }).catch((e) => { if (!controller.signal.aborted) setError(e.message); })
      .finally(() => { if (!controller.signal.aborted) setImagesLoading(false); });
    return () => controller.abort();
  }, [selected]);

  const activeId = job && RUNNING.has(job.status) ? job.request_id : null;
  useEffect(() => {
    if (job?.status === "FAILED" || job?.status === "REVIEWED") pending.current = null;
  }, [job?.status]);

  useEffect(() => {
    if (!activeId) return undefined;
    const controller = new AbortController();
    let fetching = false;
    const timer = setInterval(async () => {
      if (fetching) return;
      fetching = true;
      try {
        const data = await api(`/jobs/${activeId}`, { signal: controller.signal });
        if (!controller.signal.aborted) { setJob(data); setRevision((n) => n + 1); }
      } catch (e) {
        if (!controller.signal.aborted) setError(`상태 조회 실패: ${e.message} 게시 실패로 확정된 것은 아닙니다. 다시 조회합니다.`);
      } finally { fetching = false; }
    }, 4000);
    return () => { clearInterval(timer); controller.abort(); };
  }, [activeId]);

  const videoId = includeVideo ? selected?.video_id : null;
  const count = imageIds.length + (videoId ? 1 : 0);
  const active = busy || Boolean(activeId);
  const previewBase = process.env.REACT_APP_FASTAPI_ADS_URL || process.env.REACT_APP_FASTAPI_SEVER_URL || "";

  function select(item) {
    setSelected(item); setIncludeVideo(Boolean(item.video_id)); setImageIds([]); pending.current = null; setError("");
  }

  function toggleImage(id) {
    if (imageIds.includes(id)) setImageIds(imageIds.filter((value) => value !== id));
    else if (count >= 20) { setError("영상과 이미지를 합쳐 최대 20개 선택 가능합니다."); return; }
    else setImageIds([...imageIds, id]);
    pending.current = null;
  }

  async function start() {
    if (submitting.current || active || !selected || count < 1 || count > 20 || !config?.ready) return;
    if (!window.confirm(`@${config.username}에 '${selected.store_name}' 미디어 ${count}개를 공개 게시합니다. 진행할까요?`)) return;
    submitting.current = true; setBusy(true); setError("");
    try {
      if (!pending.current) pending.current = newId();
      trackSuccess(pending.current);
      const data = await api("/jobs", { method: "POST", body: JSON.stringify({ request_id: pending.current,
        user_id: selected.user_id, video_id: videoId || null, image_ids: imageIds, confirm_public: true }) });
      if (alive.current) { trackSuccess(data.request_id); setJob(data); if (["FAILED", "REVIEWED"].includes(data.status)) pending.current = null; }
    } catch (e) {
      if (alive.current) setError(`${e.message} 접수가 불확실하면 선택을 바꾸지 말고 기록을 확인하세요. 같은 선택의 재요청은 동일 요청 ID를 사용합니다.`);
    } finally {
      submitting.current = false;
      if (alive.current) { setBusy(false); setRevision((n) => n + 1); }
    }
  }

  async function action(record, review = false) {
    if (review && !window.confirm("Threads 프로필에서 이 게시물이 게시되지 않았음을 직접 확인했습니까? 잘못 해제하면 중복 게시될 수 있습니다.")) return;
    setBusy(true); setError("");
    try {
      const data = await api(`/jobs/${record.request_id}/${review ? "review" : "refresh"}`, {
        method: "POST", ...(review ? { body: JSON.stringify({ confirmed_unpublished: true }) } : {}),
      });
      if (alive.current) { setJob(data); if (["FAILED", "REVIEWED"].includes(data.status)) pending.current = null; setRevision((n) => n + 1); }
    } catch (e) { if (alive.current) setError(e.message); }
    finally { if (alive.current) setBusy(false); }
  }

  return <main className="cyl-page ctl-page">
    <header className="cyl-heading"><div><span>THREADS UPLOAD LAB</span><h1>쓰레드 자동업로드 테스트머신</h1>
      <p>영상·사진 선택 → 인스타 방식 AI 본문 생성 → Threads 게시. 인스타·유튜브·문자와 별도 실행.</p></div><b>VIDEO / IMAGE / CAROUSEL</b></header>
    <section className="cyl-panel"><h2>테스트 계정</h2><p>@{config?.username || "확인 중"} {config?.account_id && `· ${config.account_id}`}</p>
      {config && !config.ready && <p role="alert">{config.message}</p>}<small>본문 자동 생성 · 댓글 자동응답 안내 제외 · 최대 500자 · 미디어 합계 최대 20개</small></section>
    {error && <p className="cyl-error" role="alert">{error}</p>}
    <section className="cyl-panel"><div className="cyl-result-heading"><div><h2>매장·영상 선택</h2><small>영상 없는 매장도 사진으로 선택 가능 · 총 {gallery.total}개</small></div>
      <button className="cyl-primary" type="button" disabled={active || loading || imagesLoading || !selected || !config?.ready || count < 1 || count > 20} onClick={start}>자동업로드 실행</button></div>
      {loading ? <p role="status">목록을 불러오는 중…</p> : !gallery.items.length ? <p>저장된 미디어가 없습니다.</p> : <div className="cyl-video-grid">{gallery.items.map((item) =>
        <button type="button" className="cyl-video-card" key={`${item.user_id}:${item.video_id || "images"}`}
          disabled={active} aria-label={`${item.store_name} ${item.original_name || "사진 전용"} 선택`}
          aria-pressed={selected?.user_id === item.user_id && selected?.video_id === item.video_id} onClick={() => select(item)}>
          <div className="cyl-thumbnail"><Photo path={item.video_id ? `/videos/${item.video_id}/thumbnail` : `/stores/${item.user_id}/images/${item.cover_image_id}`} />
            <span>{item.video_id ? `${Math.round(item.duration_seconds || 0)}초` : "사진 전용"}</span></div><strong>{item.store_name}</strong></button>)}</div>}
      {gallery.total_pages > 1 && <nav className="cyl-pagination" aria-label="매장 목록 페이지">
        <button type="button" disabled={active || loading || page <= 1} onClick={() => { setPage(page - 1); setSelected(null); setImageIds([]); pending.current = null; }}>이전</button>
        <span>{page} / {gallery.total_pages}</span>
        <button type="button" disabled={active || loading || page >= gallery.total_pages} onClick={() => { setPage(page + 1); setSelected(null); setImageIds([]); pending.current = null; }}>다음</button></nav>}
      {selected && <section aria-label="선택한 매장 미디어"><div className="cyl-preview">
        {selected.video_id && <video controls preload="metadata" src={`${previewBase}/concierge/videos/${selected.video_id}/file`} aria-label="선택 영상 미리보기" />}
        <div><h3>{selected.store_name}</h3>{selected.video_id && <label className="ctl-video-toggle"><input type="checkbox" checked={includeVideo} disabled={active || (!includeVideo && count >= 20)} onChange={(e) => { setIncludeVideo(e.target.checked); pending.current = null; }} />영상 포함</label>}
          <p>{selected.original_name || "영상 없는 매장 · 사진 선택 후 게시 가능"}</p><p>선택: 영상 {videoId ? 1 : 0}개 + 사진 {imageIds.length}개 / 총 {count}개</p>
          <small>영상 포함 시 영상이 첫 번째, 사진은 클릭한 순서대로 게시. 사진 선택은 자동 게시되지 않음.</small></div></div>
        <h3>매장 저장 이미지</h3>{imagesLoading ? <p>이미지를 불러오는 중…</p> : !images.length ? <p>저장된 이미지 없음</p> : <div className="ctl-images">{images.map((item) =>
          <button type="button" key={item.file_id} disabled={active} aria-label={`${item.original_name} 이미지 선택`} aria-pressed={imageIds.includes(item.file_id)} onClick={() => toggleImage(item.file_id)}>
            <Photo path={`/stores/${selected.user_id}/images/${item.file_id}`} /><span>{imageIds.includes(item.file_id) ? `선택 ${imageIds.indexOf(item.file_id) + 1}` : "추가"}</span></button>)}</div>}
      </section>}
    </section>
    {job && <section className="cyl-panel" aria-label="테스트 결과"><div className="cyl-result-heading"><h2>{LABELS[job.status] || job.status}</h2><button type="button" disabled={busy} onClick={() => action(job)}>상태 다시 확인</button></div>
      <p>{job.store_name} · {when(job.created_at)}</p><p className="ctl-caption">{job.caption || "본문 생성 전"}</p>
      {job.error_message && <p className="cyl-error">{job.error_message}</p>}
      {job.permalink && <a href={job.permalink} target="_blank" rel="noopener noreferrer">Threads 게시물 확인</a>}
      {!job.post_id && (RUNNING.has(job.status) || job.status === "NEEDS_REVIEW") && <div><small>재시작·통신 중단 시 실제 게시 여부를 직접 확인하세요. 진행 중 작업은 해제 불가.</small><button type="button" disabled={busy} onClick={() => action(job, true)}>Threads 미게시 확인 후 재시도 허용</button></div>}
    </section>}
    <section className="cyl-panel"><div className="cyl-result-heading"><h2>최근 테스트 20건</h2><button type="button" onClick={() => setRevision((n) => n + 1)}>기록 새로고침</button></div>
      <div className="cyl-history">{jobs.map((item) => <button type="button" key={item.request_id} disabled={active} onClick={() => { setJob(item); if (["FAILED", "REVIEWED"].includes(item.status)) pending.current = null; }}><strong>{item.store_name}</strong><span>{LABELS[item.status] || item.status}</span><small>{when(item.created_at)}</small></button>)}</div>
      {!jobs.length && <p>테스트 기록 없음</p>}</section>
  </main>;
}
